import type { GameId, GameSpec } from '../api/types';

export interface GameDefinition extends GameSpec {
  /** Stable id used in preferences / routing. */
  id: GameId;
  /** Short label for pickers. */
  shortName: string;
  /** Whether the app treats this as the default for new users. */
  isDefault?: boolean;
  /** What the bonus number is called on real tickets (e.g. "Powerball", "Mega Ball", "Lucky"). */
  bonusLabel: string;
  /** Human-readable draw cadence for empty/loading UI. */
  drawDays: string;
}

/**
 * API-backed game specs used by app-wide active game selection. OCR receives the active
 * game id only as a hint; the Worker owns the supported-game list and validation ranges.
 */
export const GAMES: Record<GameId, GameDefinition> = {
  'mega-millions': {
    id: 'mega-millions',
    name: 'Mega Millions',
    shortName: 'Mega Millions',
    mainCount: 5,
    mainRange: 70,
    bonusCount: 1,
    bonusRange: 25,
    bonusLabel: 'Mega Ball',
    drawDays: 'Tue & Fri',
    isDefault: true,
  },
  powerball: {
    id: 'powerball',
    name: 'Powerball',
    shortName: 'Powerball',
    mainCount: 5,
    mainRange: 69,
    bonusCount: 1,
    bonusRange: 26,
    bonusLabel: 'Powerball',
    drawDays: 'Mon, Wed & Sat',
  },
};

export const GAME_LIST: GameDefinition[] = [GAMES['mega-millions'], GAMES.powerball];

export const DEFAULT_GAME_ID: GameId =
  GAME_LIST.find((g) => g.isDefault)?.id ?? GAME_LIST[0].id;

export function isGameId(value: unknown): value is GameId {
  return value === 'powerball' || value === 'mega-millions';
}

export function normalizeGameId(value: unknown): GameId {
  if (value === 'megamillions') return 'mega-millions';
  return isGameId(value) ? value : DEFAULT_GAME_ID;
}

export function storedGameId(value: unknown): GameId | undefined {
  if (value === 'megamillions') return 'mega-millions';
  return isGameId(value) ? value : undefined;
}

export function gameById(id: unknown): GameDefinition {
  return GAMES[normalizeGameId(id)];
}
